import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { User, Mail, Phone } from "lucide-react";
import FormField from "@/components/ui/FormField";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/Select";
import { cn } from "@/utils/cn";
import {
  ApplicationFormValues,
  EMPLOYMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
} from "@/pages/landing/application/applicationFormSchema";
import { COUNTRIES } from "@/data/countries";

interface ProgrammeOption {
  value: string;
  label: string;
}

interface CohortOption {
  value: string;
  label: string;
}

interface PersonalInformationFormProps {
  verifiedEmail?: string;
  isEmailLocked?: boolean;
  programmeOptions?: ProgrammeOption[];
  isProgrammeLoading?: boolean;
  cohortOptions?: CohortOption[];
  isCohortLoading?: boolean;
}

const PersonalInformationForm: React.FC<PersonalInformationFormProps> = ({
  verifiedEmail,
  isEmailLocked = false,
  programmeOptions = [],
  isProgrammeLoading = false,
  cohortOptions = [],
  isCohortLoading = false,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<ApplicationFormValues>();

  const personalErrors = errors.personal ?? {};
  const nationalityOptions = useMemo(
    () => [...COUNTRIES].sort((a, b) => a.localeCompare(b)),
    [],
  );

  const triggerDatePicker = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const target = event.currentTarget as HTMLInputElement & {
      showPicker?: () => void;
    };

    if (typeof target.showPicker === "function") {
      target.showPicker();
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <FormField label="First Name" required>
        <Input
          placeholder="Nosakhare"
          icon={<User size={16} />}
          {...register("personal.firstName")}
          className={cn(
            personalErrors.firstName &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        />
        {personalErrors.firstName && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.firstName.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Last Name" required>
        <Input
          placeholder="Agbonynima"
          {...register("personal.lastName")}
          className={cn(
            personalErrors.lastName &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        />
        {personalErrors.lastName && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.lastName.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Email Address" required>
        <Input
          type="email"
          placeholder="olivia@untitledui.com"
          icon={<Mail size={16} />}
          readOnly={isEmailLocked}
          {...register("personal.email")}
          className={cn(
            isEmailLocked ? "bg-gray-50 text-gray-600" : "",
            personalErrors.email &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        />
        {personalErrors.email && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.email.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Phone Number" required>
        <Input
          type="tel"
          placeholder="080 000 0000"
          icon={<Phone size={16} />}
          {...register("personal.phone")}
          className={cn(
            personalErrors.phone &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        />
        {personalErrors.phone && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.phone.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Nationality" required>
        <Select
          defaultValue=""
          {...register("personal.nationality")}
          className={cn(
            personalErrors.nationality &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            Select nationality
          </option>
          {nationalityOptions.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </Select>
        {personalErrors.nationality && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.nationality.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Date of Birth" required>
        <Input
          type="date"
          placeholder="dd/mm/yyyy"
          onFocus={triggerDatePicker}
          onClick={triggerDatePicker}
          {...register("personal.dob")}
          className={cn(
            personalErrors.dob &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        />
        {personalErrors.dob && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.dob.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Gender" required>
        <Select
          defaultValue=""
          {...register("personal.gender")}
          className={cn(
            personalErrors.gender &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            Select gender
          </option>
          {GENDER_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        {personalErrors.gender && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.gender.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Employment Status" required>
        <Select
          defaultValue=""
          {...register("personal.employmentStatus")}
          className={cn(
            personalErrors.employmentStatus &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            Select status
          </option>
          {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
        {personalErrors.employmentStatus && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.employmentStatus.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Programme" required>
        <Select
          defaultValue=""
          disabled={isProgrammeLoading || programmeOptions.length === 0}
          {...register("personal.programme")}
          className={cn(
            personalErrors.programme &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            {isProgrammeLoading ? "Loading programmes..." : "Select programme"}
          </option>
          {programmeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {personalErrors.programme && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.programme.message as string}
          </p>
        )}
      </FormField>

      <FormField label="Cohort" required>
        <Select
          defaultValue=""
          disabled={isCohortLoading || cohortOptions.length === 0}
          {...register("personal.cohort")}
          className={cn(
            personalErrors.cohort &&
              "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500",
          )}
        >
          <option value="" disabled>
            {isCohortLoading
              ? "Loading cohorts..."
              : cohortOptions.length === 0
                ? "No cohorts available"
                : "Select cohort"}
          </option>
          {cohortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {personalErrors.cohort && (
          <p className="text-xs text-red-600 mt-1">
            {personalErrors.cohort.message as string}
          </p>
        )}
      </FormField>
    </div>
  );
};

export default PersonalInformationForm;
